import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  MapPin, Globe, ArrowRight, Heart, Menu, X, Plus, User, Crown, Shield, LogOut,
  Trophy, Mail, Printer, ShoppingBag, Send, Sparkles, Coffee, Gift, HandHeart,
  Award, Share2, Lock, Check, Flame, Medal,
} from 'lucide-react';
import { MapFilterProvider } from '../contexts/MapFilterContext';
import { InteractiveMap } from './InteractiveMap';
import { FeaturedStories } from './FeaturedStories';
import { Footer } from './Footer';
import { useAuth, LoginForm, UserProfile } from './UserAuth';
import { ClubShowcase } from './ClubShowcase';
import { KindnessBadgeCelebration } from './KindnessBadgeCelebration';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Input } from './ui/input';
import api, { API_BASE } from '../services/api';
import { toast } from 'sonner';

// Card artwork (matches the printable Avery 28371 template on the backend)
const FRONT_CARD_ART = 'https://customer-assets-rejwkqb3.emergentagent.net/job_fc0afc2b-3c18-4c2f-acef-a992572ab432/artifacts/zdvppu0w_Kindness%20Card%20-%20Front.png';
const BACK_CARD_ART = 'https://customer-assets-rejwkqb3.emergentagent.net/job_fc0afc2b-3c18-4c2f-acef-a992572ab432/artifacts/kf8bag39_image.png';
// Marketing mockup with both cards side-by-side, used as the Classic
// template's picker thumbnail.
const CLASSIC_MOCKUP_ART = 'https://customer-assets-rejwkqb3.emergentagent.net/job_fc0afc2b-3c18-4c2f-acef-a992572ab432/artifacts/hwbxcf6f_image.png';

// Curated list of kind-act ideas rendered on the page
const KIND_ACT_IDEAS = [
  { icon: Coffee, title: 'Pay It Forward', text: 'Cover the coffee or meal for the person behind you in the drive-thru.' },
  { icon: HandHeart, title: 'Lend a Hand', text: 'Help someone stranded on the side of the road — jumper cables, a spare tire, a phone call.' },
  { icon: Gift, title: 'Leave a Care Package', text: 'Assemble a small gift package (snacks, socks, a note) and leave it for someone who needs a lift.' },
  { icon: Sparkles, title: 'Cook for a Neighbor', text: 'Drop off a meal or dessert for a neighbor going through a rough week.' },
  { icon: MapPin, title: 'Yard Work Surprise', text: 'Mow, shovel, or clear leaves at a neighbor\'s house — no ask, no announcement.' },
  { icon: Heart, title: 'Help an Elder', text: 'Offer to run errands, sort mail, or fix small things around the house for an older neighbor.' },
];

// ────────────────────────────────────────────────────────────
// Header — gold parchment top nav, navy accents
// ────────────────────────────────────────────────────────────
const KindnessHeader = ({ clubName }) => {
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [showUserProfile, setShowUserProfile] = useState(false);
  const { user, logout } = useAuth();
  const [userPoints, setUserPoints] = useState(0);

  useEffect(() => {
    if (user?.email) {
      api.getUserPoints(user.email)
        .then((d) => setUserPoints(d.totalPoints || 0))
        .catch(() => {});
    }
  }, [user]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 shadow-lg border-b border-amber-900/40"
        style={{
          zIndex: 1000,
          background: 'linear-gradient(90deg, #6B4A1C 0%, #8A6014 50%, #6B4A1C 100%)',
        }}
      >
        <div className="container mx-auto px-2 sm:px-4">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <button
              className="flex items-center space-x-2 hover:opacity-90 focus:outline-none bg-transparent border-0"
              onClick={() => navigate('/club/kindness')}
              type="button"
              data-testid="kindness-logo"
            >
              <span className="text-2xl sm:text-3xl">✈️</span>
              <div className="flex flex-col items-start">
                <span className="text-lg sm:text-xl font-bold text-amber-100 leading-tight drop-shadow">{clubName}</span>
                <span className="text-[10px] sm:text-xs text-amber-200/80 leading-tight">Pass the kindness forward</span>
              </div>
            </button>

            <nav className="hidden md:flex items-center space-x-4 text-amber-50">
              <button onClick={() => scrollTo('how-it-works')} className="hover:text-white text-sm font-medium" data-testid="nav-how">How It Works</button>
              <button onClick={() => scrollTo('acts')} className="hover:text-white text-sm font-medium" data-testid="nav-acts">Acts of Kindness</button>
              <button onClick={() => scrollTo('map')} className="hover:text-white text-sm font-medium" data-testid="nav-map">Live Map</button>
              <button onClick={() => scrollTo('stories')} className="hover:text-white text-sm font-medium" data-testid="nav-stories">Stories</button>
              <button onClick={() => scrollTo('badges')} className="hover:text-white text-sm font-medium" data-testid="nav-badges">Badges</button>
              <button onClick={() => scrollTo('leaderboard')} className="hover:text-white text-sm font-medium" data-testid="nav-leaderboard">Leaderboard</button>
              <button onClick={() => scrollTo('cards')} className="hover:text-white text-sm font-medium" data-testid="nav-cards">Get Cards</button>
            </nav>

            <div className="flex items-center gap-2">
              {user ? (
                <>
                  {userPoints > 0 && (
                    <div className="hidden sm:flex items-center gap-1 bg-amber-100/20 text-amber-50 border border-amber-200/40 rounded-full px-3 py-1 text-xs font-semibold">
                      <Trophy className="w-3 h-3" /> {userPoints} pts
                    </div>
                  )}
                  <button
                    onClick={() => setShowUserProfile(true)}
                    className="flex items-center gap-1 bg-amber-100/20 hover:bg-amber-100/30 text-amber-50 border border-amber-200/40 rounded-full px-3 py-1.5 text-xs font-semibold"
                    data-testid="user-profile-btn"
                  >
                    <User className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">{user.name || user.email?.split('@')[0]}</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setShowLoginForm(true)}
                  className="flex items-center gap-1 bg-amber-100 hover:bg-white text-amber-900 rounded-full px-3 py-1.5 text-xs font-semibold shadow"
                  data-testid="login-btn"
                >
                  <User className="w-3.5 h-3.5" /> Login
                </button>
              )}
              <button
                className="md:hidden text-amber-50 p-1.5"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {isMenuOpen && (
            <div className="md:hidden pb-3 border-t border-amber-200/20 pt-3 space-y-2">
              {['how-it-works', 'acts', 'map', 'stories', 'badges', 'leaderboard', 'cards'].map((sec) => (
                <button
                  key={sec}
                  onClick={() => scrollTo(sec)}
                  className="block w-full text-left text-amber-50 hover:text-white text-sm font-medium py-1"
                >
                  {sec === 'how-it-works' ? 'How It Works' : sec === 'acts' ? 'Acts of Kindness' : sec === 'map' ? 'Live Map' : sec === 'stories' ? 'Stories' : sec === 'badges' ? 'Badges' : sec === 'leaderboard' ? 'Leaderboard' : 'Get Cards'}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>

      {showLoginForm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowLoginForm(false)}>
          <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <LoginForm
              onSuccess={() => setShowLoginForm(false)}
              onClose={() => setShowLoginForm(false)}
            />
          </div>
        </div>
      )}
      {showUserProfile && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4" onClick={() => setShowUserProfile(false)}>
          <div className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <UserProfile onLogout={() => { logout(); setShowUserProfile(false); }} onClose={() => setShowUserProfile(false)} />
          </div>
        </div>
      )}
    </>
  );
};

// ────────────────────────────────────────────────────────────
// Hero — navy background with gold paper-plane accents
// ────────────────────────────────────────────────────────────
const KindnessHero = () => {
  const navigate = useNavigate();
  return (
    <section
      className="relative min-h-[640px] flex items-center px-4 py-20 overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse at 20% 10%, #2a3980 0%, #131E4F 40%, #08102A 100%)',
      }}
      data-testid="kindness-hero"
    >
      {/* Warm gold glow behind the copy */}
      <div
        className="pointer-events-none absolute -left-32 top-1/2 -translate-y-1/2 w-[520px] h-[520px] rounded-full opacity-30"
        style={{ background: 'radial-gradient(circle, #F59E0B 0%, transparent 60%)' }}
        aria-hidden="true"
      />
      {/* Subtle grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2'/></filter><rect width='120' height='120' filter='url(%23n)' opacity='0.6'/></svg>\")" }}
        aria-hidden="true"
      />

      {/* Decorative floating planes with glow */}
      <div className="absolute top-16 left-8 text-amber-300 text-4xl rotate-12 drop-shadow-[0_0_24px_rgba(245,158,11,0.5)]" aria-hidden="true">✈️</div>
      <div className="absolute bottom-24 right-1/3 text-amber-300 text-5xl -rotate-6 drop-shadow-[0_0_30px_rgba(245,158,11,0.4)]" aria-hidden="true">✈️</div>
      <div className="absolute top-1/2 left-1/3 text-amber-200/40 text-6xl rotate-45" aria-hidden="true">✈️</div>

      <div className="relative container mx-auto grid lg:grid-cols-[1.15fr_1fr] gap-12 items-center">
        <div className="text-center lg:text-left">
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full border border-amber-300/50 bg-amber-200/10 backdrop-blur shadow-[0_0_20px_rgba(245,158,11,0.15)]">
            <Heart className="w-4 h-4 text-amber-200" />
            <span className="text-amber-100 text-sm font-medium">A community of random-act travelers</span>
          </div>

          <h1
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-amber-100 leading-[1.05] mb-4"
            style={{ textShadow: '0 6px 24px rgba(0,0,0,0.55), 0 0 30px rgba(245,158,11,0.15)' }}
          >
            Commit a Random <br className="hidden sm:block" />Act of Kindness
          </h1>
          <p className="text-lg sm:text-2xl font-semibold mb-4 bg-gradient-to-r from-amber-300 via-yellow-200 to-amber-300 bg-clip-text text-transparent">
            Scan. Do good. Pass it on.
          </p>
          <p className="text-amber-100/85 max-w-2xl mx-auto lg:mx-0 mb-8 text-base sm:text-lg leading-relaxed">
            Every Kindness card carries a unique QR that follows the good deed as it travels
            from stranger to stranger. Keep it as a reminder, or pass it along and watch the
            ripple spread across the world.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
            <Button
              size="lg"
              onClick={() => {
                const el = document.getElementById('cards');
                el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-bold px-8 py-6 text-lg shadow-2xl shadow-amber-500/40 hover:-translate-y-0.5 transition-transform"
              data-testid="hero-get-cards-btn"
            >
              <Printer className="w-5 h-5 mr-2" />
              Get Your Kindness Cards
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => {
                const el = document.getElementById('map');
                el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="border-2 border-amber-200/60 text-amber-100 hover:bg-amber-100/10 px-8 py-6 text-lg backdrop-blur-sm hover:-translate-y-0.5 transition-transform"
              data-testid="hero-explore-map-btn"
            >
              <Globe className="w-5 h-5 mr-2" />
              Watch Kindness Travel
            </Button>
          </div>
        </div>

        {/* Floating card mockup — two cards stacked with drop shadow so the hero pops */}
        <div className="relative hidden lg:flex items-center justify-center h-[420px]" aria-hidden="true">
          <div
            className="absolute w-[300px] rounded-2xl overflow-hidden shadow-[0_35px_60px_-15px_rgba(0,0,0,0.7),0_20px_25px_-5px_rgba(245,158,11,0.15)] border border-amber-200/20 rotate-[-8deg] translate-x-[-40px] translate-y-[10px]"
            style={{ transform: 'rotate(-8deg) translate(-40px, 10px)' }}
          >
            <img src={BACK_CARD_ART} alt="" className="w-full h-auto block" />
          </div>
          <div
            className="absolute w-[300px] rounded-2xl overflow-hidden shadow-[0_45px_80px_-20px_rgba(0,0,0,0.85),0_25px_35px_-5px_rgba(245,158,11,0.2)] border-2 border-amber-200/30 rotate-[5deg] translate-x-[40px]"
            style={{ transform: 'rotate(5deg) translate(40px, -10px)' }}
          >
            <img src={FRONT_CARD_ART} alt="" className="w-full h-auto block" />
          </div>
          <div
            className="pointer-events-none absolute inset-x-8 bottom-8 h-6 rounded-full blur-xl opacity-70"
            style={{ background: 'radial-gradient(ellipse, rgba(245,158,11,0.35), transparent 70%)' }}
          />
        </div>
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────
// Belonging strip — 4 pillars
// ────────────────────────────────────────────────────────────
const KindnessPillars = () => (
  <section className="py-14 bg-gradient-to-b from-slate-900 to-slate-800" data-testid="kindness-pillars">
    <div className="container mx-auto px-4">
      <div className="text-center mb-10">
        <h2 className="text-2xl sm:text-3xl font-bold text-amber-100 mb-2">You belong to the club</h2>
        <p className="text-amber-100/70 max-w-2xl mx-auto">Four reasons members love KindnessProject.app</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
        {[
          { icon: MapPin, title: 'Track your kindness', text: 'See every place your card lands and every good deed logged along the way.' },
          { icon: Globe, title: 'Watch it travel', text: 'A live map of kindness spreading across cities, states, and countries.' },
          { icon: Sparkles, title: 'Be inspired', text: 'Read stories from strangers doing beautiful things and pay it forward.' },
          { icon: Award, title: 'Earn kindness badges', text: 'Unlock 1, 5, 20-act badges plus a fiery 7-day streak — celebrated on your profile.' },
        ].map((p, i) => (
          <div
            key={i}
            className="rounded-2xl p-6 border border-amber-200/20 bg-gradient-to-b from-slate-800/80 to-slate-900/60 backdrop-blur hover:border-amber-300/60 hover:-translate-y-1 transition-all shadow-xl shadow-slate-950/40 hover:shadow-amber-500/10"
            data-testid={`kindness-pillar-${i}`}
          >
            <div className="w-11 h-11 rounded-full flex items-center justify-center bg-amber-500/20 border border-amber-300/40 mb-4">
              <p.icon className="w-5 h-5 text-amber-200" />
            </div>
            <h3 className="text-amber-100 font-bold text-lg mb-2">{p.title}</h3>
            <p className="text-amber-100/70 text-sm leading-relaxed">{p.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ────────────────────────────────────────────────────────────
// Acts of Kindness ideas
// ────────────────────────────────────────────────────────────
const KindnessActs = () => (
  <section id="acts" className="py-16 bg-gradient-to-b from-amber-50 to-amber-100" data-testid="kindness-acts">
    <div className="container mx-auto px-4 max-w-6xl">
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 mb-3">Simple Acts of Kindness</h2>
        <p className="text-amber-900/80 max-w-2xl mx-auto">
          Not sure where to start? Try one of these — then log it on the card you passed on.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {KIND_ACT_IDEAS.map((a, i) => (
          <div
            key={i}
            className="rounded-2xl p-6 border border-amber-300/60 bg-white shadow-sm hover:shadow-md transition-all"
            data-testid={`kindness-act-${i}`}
          >
            <div className="w-11 h-11 rounded-xl bg-amber-100 border border-amber-300 flex items-center justify-center mb-4">
              <a.icon className="w-5 h-5 text-amber-700" />
            </div>
            <h3 className="font-bold text-amber-950 text-lg mb-2">{a.title}</h3>
            <p className="text-amber-900/80 text-sm leading-relaxed">{a.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ────────────────────────────────────────────────────────────
// Printable Kindness Cards CTA (FREE Avery 28371 PDF)
// ────────────────────────────────────────────────────────────
const TEMPLATE_OPTIONS = [
  {
    id: 'classic',
    name: 'Classic',
    tagline: 'Gold parchment · Navy back',
    accent: 'border-amber-400 ring-amber-400',
    singlePreview: CLASSIC_MOCKUP_ART,
    singlePreviewBg: '#0F172A',
  },
  {
    id: 'minimalist',
    name: 'Minimalist',
    tagline: 'Cream · Charcoal typography',
    accent: 'border-stone-300 ring-stone-400',
    frontPreview: null,
    backPreview: null,
    swatchFront: 'linear-gradient(135deg,#FAF6EE 0%,#EFE7D2 100%)',
    swatchBack: '#1F2937',
    labelFront: 'RANDOM ACT OF KINDNESS',
    labelBack: 'Keep it — or pass it on.',
  },
  {
    id: 'kids',
    name: 'Kid-Friendly',
    tagline: 'Playful gradient · Big QR',
    accent: 'border-fuchsia-400 ring-fuchsia-400',
    frontPreview: null,
    backPreview: null,
    swatchFront: 'linear-gradient(135deg,#FCD34D 0%,#FEC5BB 100%)',
    swatchBack: '#8B5CF6',
    labelFront: 'Be Kind ✈',
    labelBack: 'Kindness travels here! ✈',
  },
];

const KindnessCardsBlock = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sheetCount, setSheetCount] = useState(1);
  const [template, setTemplate] = useState('classic');
  const [preregister, setPreregister] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [personalTag, setPersonalTag] = useState('');

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isRegistered = !!user?.email;
  const maxSheets = isAdmin ? 50 : (isRegistered || preregister) ? 10 : 1;

  // Actual PDF fetch — called either directly (no preregister) or after the
  // preregister confirmation dialog resolves.
  const downloadPdf = async ({ email, contributorName, doPreregister, personalTagValue }) => {
    const clamped = Math.max(1, Math.min(maxSheets, parseInt(sheetCount, 10) || 1));
    setBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/api/kindness/print-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sheet_count: clamped,
          email: email || null,
          template,
          preregister: !!doPreregister,
          contributor_name: contributorName || null,
          personal_tag: personalTagValue || null,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Unable to generate cards');
      }
      const newMember = resp.headers.get('X-Kindness-New-Member') === '1';
      const savedTag = resp.headers.get('X-Kindness-Personal-Tag') || '';
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kindness-cards-${template}-${clamped}sheet${clamped > 1 ? 's' : ''}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      if (doPreregister && newMember) {
        toast.success(
          `Welcome to KindnessProject! Your ${clamped * 10} cards${savedTag ? ` (tag: ${savedTag})` : ''} are registered to ${email}. Use "Sign in with Magic Link" any time to see them in your dashboard.`,
          { duration: 8000 }
        );
      } else if (doPreregister) {
        toast.success(`Downloaded ${clamped * 10} Kindness cards — preregistered to ${email}${savedTag ? ` · tag: ${savedTag}` : ''}`);
      } else {
        toast.success(`Downloaded ${clamped * 10} Kindness cards — print double-sided on Avery 28371`);
      }
    } catch (e) {
      toast.error(e.message || 'PDF generation failed');
    } finally {
      setBusy(false);
    }
  };

  // Entry point when the user clicks "Download".
  //   • Preregister OFF → download PDF directly (anonymous or signed-in).
  //   • Preregister ON  → show the confirmation dialog. Signed-in users get
  //                       a summary; anonymous users get an email + name form
  //                       so they become a member as part of the flow.
  const handleGeneratePdf = () => {
    if (busy) return;
    if (preregister) {
      setGuestEmail('');
      setGuestName('');
      setPersonalTag('');
      setShowConfirmDialog(true);
      return;
    }
    downloadPdf({
      email: user?.email || null,
      contributorName: user?.name || null,
      doPreregister: false,
    });
  };

  const handleConfirmPreregister = async () => {
    const clamped = Math.max(1, Math.min(maxSheets, parseInt(sheetCount, 10) || 1));
    const tagValue = personalTag.trim();
    // Path A: hand off to /bulk-register so the user can attach a photo,
    // location, story, tag, contributor name, etc. — the exact Batch Story
    // Processing UI. The final "Complete" step in that flow will call
    // /api/kindness/print-cards-by-uids and download the PDF.
    if (isRegistered) {
      setShowConfirmDialog(false);
      try {
        const resp = await fetch(`${API_BASE}/api/kindness/allocate-cards`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sheet_count: clamped,
            email: user.email,
            template,
          }),
        });
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}));
          throw new Error(err.detail || 'Unable to allocate cards');
        }
        const data = await resp.json();
        const q = new URLSearchParams({
          kindness: '1',
          uids: (data.uids || []).join(','),
          template,
          sheets: String(clamped),
        });
        if (tagValue) q.set('privateTag', tagValue);
        toast.success(`Reserved ${data.total_cards} Kindness UIDs — fill in the batch details and we'll print the PDF at the end.`);
        navigate(`/bulk-register?${q.toString()}`);
      } catch (e) {
        toast.error(e.message || 'Unable to reserve Kindness UIDs');
      }
      return;
    }
    const email = guestEmail.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('Enter a valid email so we can register these cards to you');
      return;
    }
    setShowConfirmDialog(false);
    // Anonymous flow (Path A for guests): allocate UIDs against the guest
    // email (creating a passwordless member on the backend), then redirect to
    // /bulk-register in Kindness+guest mode so they get the full batch form
    // (photo, story, location, tag, etc.) instead of just email/name/tag.
    try {
      const resp = await fetch(`${API_BASE}/api/kindness/allocate-cards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sheet_count: clamped,
          email,
          template,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.detail || 'Unable to allocate cards');
      }
      const data = await resp.json();
      const q = new URLSearchParams({
        kindness: '1',
        uids: (data.uids || []).join(','),
        template,
        sheets: String(clamped),
        guestEmail: email,
      });
      if (guestName.trim()) q.set('guestName', guestName.trim());
      if (tagValue) q.set('privateTag', tagValue);
      // Best-effort magic-link so the new member can sign in later
      try { await api.sendMagicLink(email); } catch (_) {}
      toast.success(`Reserved ${data.total_cards} Kindness UIDs for ${email} — fill in the batch details and we'll print the PDF at the end.`);
      navigate(`/bulk-register?${q.toString()}`);
    } catch (e) {
      toast.error(e.message || 'Unable to reserve Kindness UIDs');
    }
  };

  return (
    <section id="cards" className="py-16 bg-gradient-to-b from-slate-800 to-slate-900" data-testid="kindness-cards">
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="text-center mb-10">
          <Badge className="bg-amber-300 text-amber-950 font-bold mb-3">Free · Avery 28371</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-amber-100 mb-3">Print Your Own Kindness Cards</h2>
          <p className="text-amber-100/80 max-w-2xl mx-auto">
            Every card gets a unique QR code and a home on the map the moment someone scans it.
            {isRegistered ? ' You can request up to 10 sheets (100 cards) at a time.' : ' Sign in to unlock up to 10 sheets per request.'}
          </p>
        </div>

        {/* Template picker */}
        <div className="max-w-5xl mx-auto mb-8">
          <p className="text-amber-200 font-semibold text-sm mb-3 uppercase tracking-wider">Pick a card style</p>
          <div className="grid sm:grid-cols-3 gap-4">
            {TEMPLATE_OPTIONS.map((opt) => {
              const isActive = template === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setTemplate(opt.id)}
                  className={`text-left rounded-2xl overflow-hidden border-2 transition-all ${isActive ? `${opt.accent} ring-2 shadow-2xl` : 'border-slate-700 hover:border-amber-200/40'}`}
                  data-testid={`kindness-template-${opt.id}`}
                >
                  {opt.singlePreview ? (
                    <div
                      className="h-32 flex items-center justify-center"
                      style={{ background: opt.singlePreviewBg || '#0F172A' }}
                    >
                      <img
                        src={opt.singlePreview}
                        alt={`${opt.name} card mockup`}
                        className="max-h-28 w-auto object-contain drop-shadow-2xl"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 h-32">
                      {opt.frontPreview ? (
                        <img src={opt.frontPreview} alt={`${opt.name} front`} className="object-cover w-full h-full" />
                      ) : (
                        <div
                          className="flex items-center justify-center text-center px-2 text-xs font-bold text-slate-900"
                          style={{ background: opt.swatchFront }}
                        >
                          {opt.labelFront}
                        </div>
                      )}
                      {opt.backPreview ? (
                        <img src={opt.backPreview} alt={`${opt.name} back`} className="object-cover w-full h-full" />
                      ) : (
                        <div
                          className="flex items-center justify-center text-center px-2 text-[10px] font-semibold text-white"
                          style={{ background: opt.swatchBack }}
                        >
                          {opt.labelBack}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="p-3 bg-slate-900/70 backdrop-blur">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-amber-100 font-bold text-sm">{opt.name}</div>
                        <div className="text-amber-100/60 text-xs">{opt.tagline}</div>
                      </div>
                      {isActive && (
                        <div className="w-6 h-6 rounded-full bg-amber-400 flex items-center justify-center">
                          <Check className="w-4 h-4 text-slate-900" />
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="max-w-2xl mx-auto rounded-2xl border border-amber-300/40 bg-slate-800/60 backdrop-blur p-6 sm:p-8">
          {/* Preregister — placed BEFORE the print controls so the user
              decides ownership up-front. Available to everyone: signed-in
              members get one-click; anonymous visitors are prompted for an
              email in the confirmation dialog and become a member as part
              of the flow. */}
          <label
            className="mb-5 flex items-start gap-3 rounded-xl px-4 py-3 transition-colors border bg-slate-900/50 border-amber-300/30 hover:border-amber-300/60 cursor-pointer"
            data-testid="kindness-preregister-label"
          >
            <input
              type="checkbox"
              checked={preregister}
              onChange={(e) => setPreregister(e.target.checked)}
              className="mt-1 h-4 w-4 accent-amber-500 cursor-pointer"
              data-testid="kindness-preregister-checkbox"
            />
            <span className="text-amber-100/90 text-sm leading-relaxed">
              <span className="font-semibold text-amber-100">Preregister these cards before printing</span>
              {isRegistered ? (
                <span className="block text-amber-100/60 text-xs mt-0.5">
                  Attach these {sheetCount * 10 || 10} cards to your account ({user.email}) so they show up in your dashboard immediately — no batch registration needed later.
                </span>
              ) : (
                <span className="block text-amber-100/60 text-xs mt-0.5">
                  Enter your email at checkout to claim these {sheetCount * 10 || 10} cards and become a KindnessProject member — we'll email you a magic sign-in link so you can view your cards anytime.
                </span>
              )}
            </span>
          </label>

          <div className="flex flex-col sm:flex-row gap-4 sm:items-end justify-center">
            <div>
              <label className="block text-amber-200 text-sm font-semibold mb-2">
                Sheets to print <span className="text-amber-300/70 text-xs">(10 cards per sheet, max {maxSheets})</span>
              </label>
              <input
                type="number"
                min={1}
                max={maxSheets}
                value={sheetCount}
                onChange={(e) => setSheetCount(e.target.value)}
                className="w-full sm:w-40 bg-slate-900 border border-amber-300/40 rounded-lg px-4 py-2.5 text-amber-100 focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="kindness-sheet-count"
              />
            </div>
            <Button
              onClick={handleGeneratePdf}
              disabled={busy}
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-bold px-8 py-6 shadow-xl shadow-amber-500/30 flex-1 sm:flex-none"
              data-testid="kindness-generate-pdf"
            >
              <Printer className="w-5 h-5 mr-2" />
              {busy ? 'Building PDF…' : `Download ${sheetCount * 10 || 10} Free Cards`}
            </Button>
          </div>
          <p className="text-amber-100/60 text-xs text-center mt-4">
            Prints on standard Avery 28371 / 8371 business-card blanks. Set your printer to double-sided (flip on short edge).
          </p>
        </div>

        <div className="text-center mt-8">
          <p className="text-amber-100/70 text-sm mb-3">Prefer premium pre-printed cards on cardstock?</p>
          <Button
            variant="outline"
            onClick={() => navigate('/store?theme=kindness')}
            className="border-2 border-amber-200/60 text-amber-100 hover:bg-amber-100/10"
            data-testid="kindness-shop-btn"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Order Printed Kindness Cards
          </Button>
        </div>
      </div>

      {/* Preregister confirmation dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="sm:max-w-md" data-testid="kindness-preregister-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-950">
              {isRegistered ? 'Confirm preregistration' : 'Claim your Kindness cards'}
            </DialogTitle>
            <DialogDescription className="text-amber-900/80">
              {isRegistered ? (
                <>You're about to reserve <span className="font-semibold">{Math.max(1, Math.min(maxSheets, parseInt(sheetCount, 10) || 1)) * 10} card{Math.max(1, Math.min(maxSheets, parseInt(sheetCount, 10) || 1)) * 10 === 1 ? '' : 's'}</span> under <span className="font-semibold">{user?.email}</span>. On the next screen you'll add a photo, story, location and any tags — just like Batch Story Processing. Once you save, the printable PDF will download automatically.</>
              ) : (
                <>Enter your email to attach these {Math.max(1, Math.min(maxSheets, parseInt(sheetCount, 10) || 1)) * 10} cards to a new KindnessProject account. We'll email you a magic sign-in link so you can view and manage them anytime.</>
              )}
            </DialogDescription>
          </DialogHeader>
          {!isRegistered && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-amber-900 mb-1">Your email *</label>
                <Input
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  className="border-amber-300 focus-visible:ring-amber-500"
                  data-testid="kindness-preregister-email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-amber-900 mb-1">
                  Your name <span className="text-amber-700/70 font-normal">(optional)</span>
                </label>
                <Input
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="How should we address you?"
                  className="border-amber-300 focus-visible:ring-amber-500"
                  data-testid="kindness-preregister-name-input"
                  maxLength={80}
                />
              </div>
            </div>
          )}
          {/* Private tag — appended to the item's `privateTag` field
              (space-separated) so a single indexed value powers both the
              Kindness club routing and personal-filter lookups. */}
          <div className={!isRegistered ? 'mt-3' : ''}>
            <label className="block text-xs font-semibold text-amber-900 mb-1">
              Private tag <span className="text-amber-700/70 font-normal">(optional)</span>
            </label>
            <Input
              type="text"
              value={personalTag}
              onChange={(e) => setPersonalTag(e.target.value)}
              placeholder="e.g. 2026-birthday-trip"
              className="border-amber-300 focus-visible:ring-amber-500"
              data-testid="kindness-preregister-personaltag-input"
              maxLength={40}
            />
            <p className="text-[11px] text-amber-800/70 mt-1">
              Appended to each card's private tag so <em>only your cards</em> can be filtered by this label later. Letters, numbers and dashes only.
            </p>
          </div>
          {!isRegistered && (
            <p className="text-[11px] text-amber-800/70 italic mt-2">
              By continuing you're creating a free KindnessProject account. No password required — sign in later with the magic link we send you.
            </p>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              className="border-amber-300 text-amber-900"
              data-testid="kindness-preregister-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmPreregister}
              disabled={busy}
              className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-bold"
              data-testid="kindness-preregister-confirm"
            >
              {busy ? 'Working…' : (isRegistered ? 'Continue to Details →' : 'Claim & Download')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

// ────────────────────────────────────────────────────────────
// Kindness Badges — 1 / 5 / 20 acts logged
// (renders locked/unlocked cards + fires a celebration modal on unlock)
// ────────────────────────────────────────────────────────────
const KindnessBadges = () => {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [celebrationBadge, setCelebrationBadge] = useState(null);
  const knownRef = useRef(null); // localStorage-backed "already-celebrated" set

  const loadBadges = async (opts = { celebrate: true }) => {
    if (!user?.email) {
      setState(null);
      return;
    }
    setLoading(true);
    try {
      const resp = await fetch(`${API_BASE}/api/kindness/badges?email=${encodeURIComponent(user.email)}`);
      const data = await resp.json();
      setState(data);

      // Celebration detection
      const storageKey = `kindnessBadgesSeen:${(user.email || '').toLowerCase()}`;
      const stored = knownRef.current ?? JSON.parse(localStorage.getItem(storageKey) || '[]');
      const earnedIds = (data.badges || []).filter((b) => b.earned).map((b) => b.id);

      // Seed on first render (don't celebrate on page load for pre-existing badges)
      if (knownRef.current === null) {
        knownRef.current = earnedIds;
        localStorage.setItem(storageKey, JSON.stringify(earnedIds));
      } else if (opts.celebrate) {
        const brandNew = earnedIds.filter((id) => !stored.includes(id));
        if (brandNew.length > 0) {
          const winner = (data.badges || []).find((b) => b.id === brandNew[brandNew.length - 1]);
          if (winner) setCelebrationBadge(winner);
        }
        knownRef.current = earnedIds;
        localStorage.setItem(storageKey, JSON.stringify(earnedIds));
      }
    } catch (_) {
      // network hiccup — keep whatever we had
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    knownRef.current = null; // reset when user changes
    loadBadges({ celebrate: false });
    // Poll every 30s so a celebration fires without needing a page refresh
    // when the user submits a kindness story in another tab.
    if (!user?.email) return;
    const t = setInterval(() => loadBadges({ celebrate: true }), 30000);
    return () => clearInterval(t);
  }, [user?.email]);

  const shareBadge = async (badge) => {
    const shareText = `I just earned "${badge.title}" ${badge.emoji} on KindnessProject.app — commit a random act of kindness and follow it around the world.`;
    const url = state?.share_url || 'https://kindnessproject.app';
    if (navigator.share) {
      try {
        await navigator.share({ title: badge.title, text: shareText, url });
        return;
      } catch (_) { /* user canceled */ }
    }
    try {
      await navigator.clipboard.writeText(`${shareText} ${url}`);
      toast.success('Shareable message copied to your clipboard');
    } catch (_) {
      toast.error('Unable to share');
    }
  };

  return (
    <section id="badges" className="py-16 bg-gradient-to-b from-amber-50 to-amber-100" data-testid="kindness-badges-section">
      <KindnessBadgeCelebration
        badge={celebrationBadge}
        onClose={() => setCelebrationBadge(null)}
        shareUrl={state?.share_url}
      />
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="text-center mb-8">
          <Badge className="bg-amber-950 text-amber-100 font-bold mb-3">Kindness Badges</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 mb-3">Earn badges as you spread kindness</h2>
          <p className="text-amber-900/80 max-w-2xl mx-auto">
            Every kindness story you log adds up. Hit 1, 5, or 20 acts to unlock a shareable badge and keep the streak alive.
          </p>
        </div>

        {!user?.email && (
          <div className="rounded-xl border-2 border-dashed border-amber-400 bg-white/70 p-6 text-center mb-6" data-testid="kindness-badges-signin-hint">
            <Award className="w-8 h-8 text-amber-700 mx-auto mb-2" />
            <p className="text-amber-900 font-semibold">Sign in to track your Kindness badges</p>
            <p className="text-amber-900/70 text-sm">Your kindness stories will automatically count toward the 1 / 5 / 20-act badges.</p>
          </div>
        )}

        {user?.email && (
          <div className="mb-4 text-center">
            {loading ? (
              <p className="text-amber-900/70 text-sm">Checking your badges…</p>
            ) : (
              <>
                <p className="text-amber-900 text-sm font-semibold">
                  You've logged <span className="text-amber-700 font-bold">{state?.kindness_count ?? 0}</span> {state?.kindness_count === 1 ? 'act' : 'acts'} of kindness
                </p>
                {state?.next_badge && (
                  <p className="text-amber-900/70 text-xs mt-1">
                    {state.next_badge.remaining} more to unlock <span className="font-semibold">{state.next_badge.title}</span> {state.next_badge.emoji}
                  </p>
                )}
              </>
            )}
          </div>
        )}

        <div className="grid sm:grid-cols-3 gap-4">
          {(state?.badges || [
            { id: 'kindness_first', threshold: 1, title: 'First Act of Kindness', description: 'You logged your very first kindness.', emoji: '✨', earned: false, progress: 0 },
            { id: 'kindness_five', threshold: 5, title: 'Kindness Streak', description: 'Five kind acts logged.', emoji: '🌟', earned: false, progress: 0 },
            { id: 'kindness_twenty', threshold: 20, title: 'Kindness Champion', description: 'Twenty acts of kindness on the map.', emoji: '🏆', earned: false, progress: 0 },
          ]).map((b) => (
            <div
              key={b.id}
              className={`rounded-2xl p-6 border-2 transition-all ${b.earned ? 'border-amber-500 bg-white shadow-xl' : 'border-amber-300/60 bg-white/60'}`}
              data-testid={`kindness-badge-${b.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${b.earned ? 'bg-gradient-to-br from-amber-400 to-yellow-500 shadow-lg shadow-amber-400/50' : 'bg-amber-100'}`}>
                  {b.earned ? b.emoji : <Lock className="w-6 h-6 text-amber-700/60" />}
                </div>
                <div className={`text-xs font-semibold px-2 py-1 rounded ${b.earned ? 'bg-amber-100 text-amber-900' : 'bg-amber-50 text-amber-700/70'}`}>
                  {b.threshold} {b.threshold === 1 ? 'act' : 'acts'}
                </div>
              </div>
              <h3 className={`font-bold text-lg mb-1 ${b.earned ? 'text-amber-950' : 'text-amber-900/70'}`}>{b.title}</h3>
              <p className={`text-sm leading-relaxed mb-3 ${b.earned ? 'text-amber-900/90' : 'text-amber-900/60'}`}>{b.description}</p>
              <div className="h-2 rounded-full bg-amber-100 overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-amber-500 to-yellow-500 transition-all"
                  style={{ width: `${Math.min(100, ((b.progress || 0) / b.threshold) * 100)}%` }}
                />
              </div>
              {b.earned ? (
                <Button
                  size="sm"
                  onClick={() => shareBadge(b)}
                  className="w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-bold"
                  data-testid={`kindness-share-${b.id}`}
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share this badge
                </Button>
              ) : (
                <p className="text-center text-amber-900/60 text-xs">
                  {Math.max(0, b.threshold - (b.progress || 0))} more to unlock
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────
// Message the Giver — anonymous thank-you relay
// ────────────────────────────────────────────────────────────
const KindnessLeaderboard = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API_BASE}/api/kindness/leaderboard?limit=10`)
      .then((r) => r.json())
      .then((d) => { if (alive) setState(d); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const leaders = state?.leaders || [];

  return (
    <section id="leaderboard" className="py-16 bg-gradient-to-b from-amber-100 to-amber-50" data-testid="kindness-leaderboard-section">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-8">
          <Badge className="bg-amber-950 text-amber-100 font-bold mb-3">Monthly Leaderboard</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 mb-3">This month's kindest members</h2>
          <p className="text-amber-900/80 max-w-2xl mx-auto">
            The people spreading the most kindness in <span className="font-semibold">{state?.month_label || 'this month'}</span>. Log a new act and see your name climb.
          </p>
        </div>

        <div className="rounded-2xl border-2 border-amber-300 bg-white shadow-lg overflow-hidden">
          {loading && (
            <p className="p-8 text-center text-amber-900/70 text-sm" data-testid="leaderboard-loading">Loading leaderboard…</p>
          )}
          {!loading && leaders.length === 0 && (
            <div className="p-10 text-center" data-testid="leaderboard-empty">
              <Trophy className="w-10 h-10 text-amber-400 mx-auto mb-3" />
              <p className="text-amber-900 font-semibold">No kindness logged this month yet</p>
              <p className="text-amber-800/70 text-sm mt-1">Be the first — scan a Kindness card and log your act.</p>
            </div>
          )}
          {!loading && leaders.length > 0 && (
            <ol className="divide-y divide-amber-200">
              {leaders.map((l) => {
                const isTop = l.rank <= 3;
                const medalColor = l.rank === 1 ? 'text-yellow-500' : l.rank === 2 ? 'text-slate-400' : l.rank === 3 ? 'text-amber-700' : 'text-amber-300';
                return (
                  <li
                    key={`${l.rank}-${l.name}`}
                    className={`flex items-center gap-4 px-5 py-3 ${isTop ? 'bg-gradient-to-r from-amber-50 to-white' : ''}`}
                    data-testid={`leaderboard-row-${l.rank}`}
                  >
                    <div className={`w-10 h-10 flex-shrink-0 rounded-full flex items-center justify-center font-bold text-lg ${isTop ? 'bg-amber-100' : 'bg-amber-50'}`}>
                      {isTop ? <Medal className={`w-5 h-5 ${medalColor}`} /> : <span className="text-amber-800/70">{l.rank}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-amber-950 truncate">{l.name}</div>
                      {l.last_location && (
                        <div className="text-xs text-amber-800/60 truncate flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {l.last_location}
                        </div>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex items-baseline gap-1">
                      <span className="text-lg font-bold text-amber-700">{l.count}</span>
                      <span className="text-xs text-amber-800/60">{l.count === 1 ? 'act' : 'acts'}</span>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </section>
  );
};

const KindnessMessageGiver = () => {
  const { user } = useAuth();
  const [uid, setUid] = useState('');
  const [fromName, setFromName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user?.name) setFromName(user.name);
    if (user?.email) setFromEmail(user.email);
  }, [user?.email, user?.name]);

  const handleSend = async () => {
    if (busy) return;
    const cleanUid = (uid || '').trim();
    const cleanMsg = (message || '').trim();
    if (!cleanUid) return toast.error('Please enter your card code');
    if (!cleanMsg) return toast.error('Please write a message');
    if (cleanMsg.length > 1500) return toast.error('Message is too long (max 1500 chars)');

    setBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/api/kindness/message-giver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          item_uid: cleanUid,
          message: cleanMsg,
          from_name: fromName || null,
          from_email: fromEmail || null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || 'Unable to send message');
      }
      if (data.delivered) {
        toast.success("Message delivered anonymously — you're a gem 💛");
      } else {
        toast.success('Message received. If the giver has an email on file, it just landed in their inbox.');
      }
      setMessage('');
    } catch (e) {
      toast.error(e.message || 'Message failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="message-giver" className="py-16 bg-gradient-to-b from-amber-100 to-amber-50" data-testid="kindness-message-giver">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="text-center mb-8">
          <Badge className="bg-amber-950 text-amber-100 font-bold mb-3">Message the Giver</Badge>
          <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 mb-3">Say thank you — anonymously</h2>
          <p className="text-amber-900/80 max-w-2xl mx-auto">
            Received a Kindness card? Enter the code from the card and write a note. We'll deliver it to
            whoever gave it to you — <em>no email addresses are shared</em>.
          </p>
        </div>

        <div className="rounded-2xl border-2 border-amber-300 bg-white shadow-lg p-6 sm:p-8">
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-amber-900 text-sm font-semibold mb-1">
                Card code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={uid}
                onChange={(e) => setUid(e.target.value)}
                placeholder="e.g. K4qR7"
                className="w-full px-4 py-2.5 rounded-lg border-2 border-amber-200 focus:border-amber-500 focus:outline-none text-amber-950"
                maxLength={12}
                data-testid="msg-giver-uid"
              />
              <p className="text-xs text-amber-700/70 mt-1">Printed on the back of the card, near the QR.</p>
            </div>
            <div>
              <label className="block text-amber-900 text-sm font-semibold mb-1">Your name</label>
              <input
                type="text"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder="Someone kind"
                className="w-full px-4 py-2.5 rounded-lg border-2 border-amber-200 focus:border-amber-500 focus:outline-none text-amber-950"
                maxLength={80}
                data-testid="msg-giver-name"
              />
            </div>
          </div>
          <label className="block text-amber-900 text-sm font-semibold mb-1">Your message <span className="text-red-500">*</span></label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="What did this kindness mean to you?"
            rows={5}
            className="w-full px-4 py-3 rounded-lg border-2 border-amber-200 focus:border-amber-500 focus:outline-none text-amber-950 resize-y"
            maxLength={1500}
            data-testid="msg-giver-message"
          />
          <p className="text-xs text-amber-700/70 mt-1 text-right">{message.length}/1500</p>

          <label className="block text-amber-900 text-sm font-semibold mb-1 mt-3">
            Your email <span className="text-amber-700/70 font-normal">(optional — only shared if you want a reply)</span>
          </label>
          <input
            type="email"
            value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)}
            placeholder="you@example.com"
            className="w-full px-4 py-2.5 rounded-lg border-2 border-amber-200 focus:border-amber-500 focus:outline-none text-amber-950"
            data-testid="msg-giver-email"
          />

          <Button
            onClick={handleSend}
            disabled={busy}
            size="lg"
            className="mt-5 w-full bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-900 font-bold shadow-lg"
            data-testid="msg-giver-send"
          >
            <Send className="w-4 h-4 mr-2" />
            {busy ? 'Sending…' : 'Send Anonymous Thank-You'}
          </Button>
          <p className="text-xs text-amber-700/70 text-center mt-3">
            Your email is never revealed to the giver unless you enter it above.
          </p>
        </div>
      </div>
    </section>
  );
};

// ────────────────────────────────────────────────────────────
// How It Works timeline
// ────────────────────────────────────────────────────────────
const KindnessHow = () => (
  <section id="how-it-works" className="py-16 bg-gradient-to-b from-amber-100 to-amber-50" data-testid="kindness-how">
    <div className="container mx-auto px-4 max-w-5xl">
      <div className="text-center mb-10">
        <h2 className="text-3xl sm:text-4xl font-bold text-amber-950 mb-3">How KindnessProject Works</h2>
        <p className="text-amber-900/80 max-w-2xl mx-auto">Two ways to use a card. Both spread kindness.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl p-6 border-2 border-amber-300 bg-white shadow-sm">
          <div className="text-4xl mb-3">🎯</div>
          <h3 className="font-bold text-xl text-amber-950 mb-2">Keep it as a reminder</h3>
          <p className="text-amber-900/80 text-sm leading-relaxed">
            Carry a card and scan it each time you do something kind — for a stranger, a neighbor,
            a coworker. Your card becomes a personal kindness journal you can share with the world.
          </p>
        </div>
        <div className="rounded-2xl p-6 border-2 border-amber-300 bg-white shadow-sm">
          <div className="text-4xl mb-3">✈️</div>
          <h3 className="font-bold text-xl text-amber-950 mb-2">Pass it forward</h3>
          <p className="text-amber-900/80 text-sm leading-relaxed">
            Do something kind and hand your card to the recipient. Ask them to log their kindness
            and pass it on. Watch it hop from city to city on the live map.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// ────────────────────────────────────────────────────────────
// Main page
// ────────────────────────────────────────────────────────────
export const KindnessClubPage = () => {
  const [searchParams] = useSearchParams();
  const searchTag = searchParams.get('search') || 'kindness';

  const kindnessTheme = {
    primary: 'amber',
    secondary: 'yellow',
    accent: 'amber',
    gradientStyle: 'linear-gradient(to right, #d97706, #f59e0b)',
    primaryColor: '#d97706',
    secondaryColor: '#f59e0b',
    iconColor: '#b45309',
  };

  return (
    <MapFilterProvider initialSearchTerm={searchTag} isPrivateTagFilter={true} theme={kindnessTheme}>
      <div className="min-h-screen bg-slate-900" data-testid="kindness-club-page">
        <KindnessHeader clubName="KindnessProject.app" />
        <div className="h-14 sm:h-16" />

        <KindnessHero />
        <KindnessPillars />
        <KindnessHow />
        <KindnessActs />

        {/* Map */}
        <div id="map" className="bg-slate-100">
          <div className="container mx-auto px-4 py-8">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-amber-950 mb-2">🗺️ Kindness Traveling Now</h2>
              <p className="text-amber-900/80">Every pin is a Kindness card in motion.</p>
            </div>
            <InteractiveMap />
          </div>
        </div>

        {/* Stories */}
        <div id="stories" className="bg-gradient-to-b from-amber-50 to-slate-50">
          <div className="container mx-auto px-4 py-10">
            <div className="text-center mb-6">
              <h2 className="text-3xl font-bold text-amber-950 mb-2">📖 Kindness Stories</h2>
              <p className="text-amber-900/80">Real acts of kindness logged by the community.</p>
            </div>
            <FeaturedStories />
          </div>
        </div>

        <KindnessCardsBlock />

        <KindnessBadges />

        <KindnessLeaderboard />

        <ClubShowcase excludeClubId="kindness" title="Explore More Communities" />
        <Footer />
      </div>
    </MapFilterProvider>
  );
};

export default KindnessClubPage;
